import { AU_KM } from "../data/planets";
import { fill, formatNumber, formatSignificant, messages, type Lang } from "../i18n/index";

/** Newton's constant in km³ kg⁻¹ s⁻² (CODATA 2018), to turn GM into a mass. */
const G = 6.6743e-20;
const SUPERSCRIPT_DIGITS = "⁰¹²³⁴⁵⁶⁷⁸⁹";
const MINUS = "−";

function superscript(n: number): string {
  return [...String(n)].map((c) => (c === "-" ? "⁻" : SUPERSCRIPT_DIGITS[Number(c)])).join("");
}

/** Mass from GM: "5.97 × 10²⁴ kg". */
export function formatMass(gm: number, lang: Lang): string {
  const kg = gm / G;
  let exponent = Math.floor(Math.log10(kg));
  let mantissa = kg / 10 ** exponent;
  // 9.996 would print as "10.00"; carry it into the exponent
  if (Number(mantissa.toFixed(2)) >= 10) {
    mantissa /= 10;
    exponent += 1;
  }
  return `${formatNumber(lang, mantissa, 2)} × 10${superscript(exponent)} kg`;
}

/** Surface gravity GM/r²: "9.80 m/s²". */
export function formatGravity(gm: number, radiusKm: number, lang: Lang): string {
  return `${formatSignificant(lang, (gm / radiusKm ** 2) * 1000, 3)} m/s²`;
}

export function formatDensity(gcm3: number, lang: Lang): string {
  return `${formatNumber(lang, gcm3, 2)} g/cm³`;
}

/** Whole degrees with a real minus sign: "−65 °C"; −0.3 reads "0 °C". */
export function formatTemperature(celsius: number, lang: Lang): string {
  const whole = Math.round(celsius);
  return `${whole < 0 ? MINUS : ""}${formatNumber(lang, Math.abs(whole))} °C`;
}

/** Semi-major axis: AU and million km for bodies around the Sun, km for moons. */
export function formatDistance(aKm: number, aroundSun: boolean, lang: Lang): string {
  if (!aroundSun) return `${formatNumber(lang, aKm)} km`;
  return fill(messages(lang).info.auDistance, {
    au: formatNumber(lang, aKm / AU_KM, 2),
    millionKm: formatNumber(lang, aKm / 1e6, 1),
  });
}

/**
 * Time-averaged orbital speed in km/s: the ellipse perimeter over the period. The perimeter uses
 * Ramanujan's second approximation, which reduces to 2πa on a circle.
 */
export function orbitalSpeedKmS(aKm: number, e: number, periodDays: number): number {
  const b = aKm * Math.sqrt(1 - e * e);
  const h = ((aKm - b) / (aKm + b)) ** 2;
  const perimeter = Math.PI * (aKm + b) * (1 + (3 * h) / (10 + Math.sqrt(4 - 3 * h)));
  return perimeter / (periodDays * 86400);
}

export function formatKmPerSecond(kmS: number, lang: Lang, digits = 1): string {
  return `${formatNumber(lang, kmS, digits)} km/s`;
}

/** One decimal; JPL's −0.00002° for Earth reads 0.0°, not −0.0°. */
export function formatInclination(deg: number, lang: Lang): string {
  return `${formatNumber(lang, Math.abs(deg), 1)}°`;
}
